import React, { useMemo, useState, useEffect, useCallback } from 'react';
import styles from './css/index.module.css';
import { Dismiss16Regular, Dismiss20Regular, Wand16Regular, Wand20Regular, ArrowClockwise16Regular } from '@fluentui/react-icons';
import { useSelector, useDispatch } from 'react-redux';
import { formatDateDifferenc, getExtensionIcon, getFileSize, getFileTypeByExtension } from '../../utils/commonUtils';
import { format } from 'date-fns';
import { OverlayDrawer, DrawerBody } from '@fluentui/react-components';
import { GetAIDocumentAnalysis, GetLatestDocumentAnalysis } from '../../redux/dataroomSlice';
import { parseHierarchicalKeywordLabels, hasHierarchicalKeywords } from '../../utils/dataroomKeywords';

const FileInfoSidebar = ({ setFileInfoSidebar, setShowSidebar, selectedFolders, selectedFile, setSelectedFile }) => {
    const dispatch = useDispatch();
    const { projectUsersAll } = useSelector(state => state?.team);
    const { selectedProject } = useSelector(state => state?.projectData);
    const { aiAnalysisData, aiAnalysisLoading, aiAnalysisError } = useSelector(state => {
        return state?.dataroom || {};
    });
    const { workstreams } = useSelector(state => state?.dataroom || {});
    const [showLensAIAnalysis, setShowLensAIAnalysis] = useState(false);
    const [selectedAnalysisTab, setSelectedAnalysisTab] = useState('executive-summary');
    const [currentColorIndex, setCurrentColorIndex] = useState(0);

    const selectedFolder = useMemo(() => {
        return selectedFolders?.length > 0 ? selectedFolders[selectedFolders.length - 1] : {};
    }, [selectedFolders]);

    const handleClose = () => {
        setFileInfoSidebar(false);
        setShowSidebar(false);
        setSelectedFile();
    };
    const getUserDetail = id => {
        let addedBy = projectUsersAll?.find(item => item?.value?.group_member_id === id);
        return (
            <>
                <p className={styles.smallFont + ' m-0'}>{addedBy?.value?.full_name || 'Project Admin'} </p>
                <p className={styles.smallFont + ' m-0'}>{addedBy?.value?.organisation}</p>
            </>
        );
    };
    const getUserNameByGroupMemberId = id => {
        let user = projectUsersAll?.find(item => item?.value?.group_member_id === id);
        return <p className={styles.smallFont + ' m-0'}>{user?.value?.full_name}</p>;
    };

    // Function to call AI Analysis API
    const callAIAnalysis = useCallback(() => {
        if (!selectedFile || !selectedProject) {
            return;
        }

        const projectId = selectedProject.id;
        const candidateIds = [selectedFolder?.ws_id, selectedFolder?.workstream_id, selectedFile?.workstream_id, selectedFile?.ws_id, selectedFile?.workstreamId];
        let wsId = candidateIds.find(v => v !== undefined && v !== null && !Number.isNaN(parseInt(v, 10)));
        if (wsId !== undefined && wsId !== null) {
            wsId = parseInt(wsId, 10);
        }

        // If only a workstream name is available (e.g., 'Financial'), map it to id from redux
        if ((wsId === undefined || wsId === null || Number.isNaN(wsId)) && typeof selectedFile?.workstream === 'string') {
            const matchByName = (workstreams || []).find(ws => ws?.name === selectedFile?.workstream || ws?.workstream === selectedFile?.workstream);
            if (matchByName?.id) wsId = matchByName.id;
        }

        // Fallback to localStorage persisted workstream id
        if (wsId === undefined || wsId === null || Number.isNaN(wsId)) {
            const wsFromLocal = typeof localStorage !== 'undefined' ? localStorage.getItem('workstream') : undefined;
            const parsed = wsFromLocal ? parseInt(wsFromLocal, 10) : NaN;
            if (!Number.isNaN(parsed)) wsId = parsed;
        }
        const documentId = selectedFile.id;

        if (!wsId) {
            console.error('❌ Workstream ID not found after fallbacks');
            return;
        }

        const documentUrl = selectedFile.document_url || selectedFile.document || selectedFile.document_link;

        // First try to get the latest analysis (without generating)
        dispatch(
            GetLatestDocumentAnalysis({
                projectId,
                wsId,
                documentId,
            }),
        );
    }, [selectedFile, selectedProject, selectedFolder, workstreams, dispatch]);

    // Function to generate new analysis (called when no existing analysis found)
    const generateNewAnalysis = useCallback(() => {
        if (!selectedFile || !selectedProject) return;

        // Clear current analysis data to ensure fresh display
        dispatch({ type: 'irlSlice/clearAIAnalysis' });

        const projectId = selectedProject.id;
        const candidateIds = [selectedFolder?.ws_id, selectedFolder?.workstream_id, selectedFile?.workstream_id, selectedFile?.ws_id, selectedFile?.workstreamId];
        let wsId = candidateIds.find(v => v !== undefined && v !== null && !Number.isNaN(parseInt(v, 10)));
        if (wsId !== undefined && wsId !== null) {
            wsId = parseInt(wsId, 10);
        }

        if ((wsId === undefined || wsId === null || Number.isNaN(wsId)) && typeof selectedFile?.workstream === 'string') {
            const matchByName = (workstreams || []).find(ws => ws?.name === selectedFile?.workstream || ws?.workstream === selectedFile?.workstream);
            if (matchByName?.id) wsId = matchByName.id;
        }

        if (wsId === undefined || wsId === null || Number.isNaN(wsId)) {
            const wsFromLocal = typeof localStorage !== 'undefined' ? localStorage.getItem('workstream') : undefined;
            const parsed = wsFromLocal ? parseInt(wsFromLocal, 10) : NaN;
            if (!Number.isNaN(parsed)) wsId = parsed;
        }

        const documentId = selectedFile.id;
        const documentUrl = selectedFile.document_url || selectedFile.document || selectedFile.document_link;

        dispatch(
            GetAIDocumentAnalysis({
                projectId,
                wsId,
                documentId,
                documentUrl,
                regenerate: true, // Force regeneration, bypass cache
            }),
        );
    }, [selectedFile, selectedProject, selectedFolder, workstreams, dispatch]);

    // Call AI Analysis when sidebar opens
    useEffect(() => {
        if (showLensAIAnalysis) {
            callAIAnalysis();
        }
    }, [showLensAIAnalysis, callAIAnalysis]);

    // Handle when no existing analysis is found
    useEffect(() => {
        if (aiAnalysisData && aiAnalysisData.should_generate) {
            generateNewAnalysis();
        }
    }, [aiAnalysisData, generateNewAnalysis]);

    // Color cycling effect for loader
    useEffect(() => {
        let intervalId;

        if (aiAnalysisLoading) {
            // Start cycling through colors when loading is true
            intervalId = setInterval(() => {
                setCurrentColorIndex(prevIndex => (prevIndex === 2 ? 0 : prevIndex + 1));
            }, 1000); // Change color every 1 second
        } else {
            // Reset color index when loading is false
            setCurrentColorIndex(0);
            clearInterval(intervalId); // Clear interval when loading becomes false
        }

        return () => clearInterval(intervalId); // Cleanup interval on component unmount
    }, [aiAnalysisLoading]);

    const colors = ['#8E2DE2', '#F8047E', '#192BC2'];
    const currentColor = colors[currentColorIndex];

    // Function to parse AI analysis content by section
    const parseAnalysisContent = (content, section) => {
        if (!content) return '';

        const lines = content.split('\n');
        let sectionContent = '';
        let inSection = false;

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];

            if (section === 'executive-summary' && line.includes('🔍 **Executive Summary**')) {
                inSection = true;
                continue;
            } else if (section === 'strengths' && line.includes('✅ **Strengths**')) {
                inSection = true;
                continue;
            } else if (section === 'risks-gaps' && line.includes('⚠️ **Risks & Gaps**')) {
                inSection = true;
                continue;
            } else if (section === 'additional-insights' && line.includes('🧩 **Additional Insights by Dimension**')) {
                inSection = true;
                continue;
            } else if (section === 'key-questions' && line.includes('❓ **Key Questions to Ask**')) {
                inSection = true;
                continue;
            }

            // Check if we've moved to the next section
            if (
                inSection &&
                (line.includes('✅ **Strengths**') ||
                    line.includes('⚠️ **Risks & Gaps**') ||
                    line.includes('🧩 **Additional Insights by Dimension**') ||
                    line.includes('❓ **Key Questions to Ask**'))
            ) {
                break;
            }

            if (inSection) {
                sectionContent += line + '\n';
            }
        }

        // Sanitize: remove trailing separators (---) and any concluding section
        let sanitized = sectionContent
            .split('\n')
            .filter(l => {
                const t = (l || '').trim();
                // drop pure separator lines like '---' or '—'
                return t !== '---' && t !== '—' && t !== '–––';
            })
            .join('\n')
            .trim();

        // If a Conclusion heading slipped into the slice, cut everything from it
        const lower = sanitized.toLowerCase();
        const conclIdx = lower.indexOf('conclusion');
        if (conclIdx !== -1) {
            sanitized = sanitized.slice(0, conclIdx).trim();
        }

        // Remove any trailing dashes still present at the very end
        sanitized = sanitized.replace(/[-–—]{3,}\s*$/g, '').trim();

        return sanitized;
    };

    // Helper to get HTML for each tab (new format)
    const getHtmlForTab = () => {
        if (!aiAnalysisData) return '';

        // Check if we have the new sections format
        if (aiAnalysisData.sections) {
            switch (selectedAnalysisTab) {
                case 'executive-summary':
                    return aiAnalysisData.sections.executive_summary_html || '';
                case 'strengths':
                    return aiAnalysisData.sections.strengths_html || '';
                case 'risks-gaps':
                    return aiAnalysisData.sections.risks_gaps_html || '';
                case 'additional-insights':
                    return aiAnalysisData.sections.additional_insights_html || '';
                case 'key-questions':
                    return aiAnalysisData.sections.key_questions_html || '';
                default:
                    return '';
            }
        }

        // Fallback to old format parsing
        return parseAnalysisContent(aiAnalysisData.ai_analysis, selectedAnalysisTab);
    };

    return (
        <div className={styles.sidebarWhite + ' p-0'}>
            <div className={styles.fileSidebarHeader}>
                <div className="d-flex">
                    {getExtensionIcon(
                        (selectedFile?.document_name || selectedFile?.document || selectedFile?.document_link)?.split('?')[0]?.split('.').pop(),
                        20,
                        20,
                        selectedFile?.document_link,
                    )}
                    <div className={styles.fileInfoName}>
                        <p className="mb-2">
                            {selectedFile?.ws_folder_prefix + '.'} {selectedFile?.item_num}{' '}
                        </p>
                        <span> {selectedFile?.document_name}</span>
                    </div>
                </div>
                <Dismiss16Regular onClick={handleClose} role="button" />
            </div>
            <div className={styles.fileSidebarBody}>
                <div>
                    <label className={styles.fileSidebarLabel}>Type</label>
                    <p>{getFileTypeByExtension((selectedFile?.document_name || selectedFile?.document || selectedFile?.document_link)?.split('?')[0]?.split('.').pop())}</p>
                </div>
                <div>
                    <label className={styles.fileSidebarLabel}>File Size</label>
                    <p>{getFileSize(selectedFile?.metadata_storage_size)}</p>
                </div>
                <div>
                    <label className={styles.fileSidebarLabel}>Location</label>
                    <p>{selectedFile?.filePath}</p>
                </div>
                <div>
                    <label className={styles.fileSidebarLabel}>Uploaded on</label>
                    <p>
                        {selectedFile?.created && format(new Date(selectedFile?.created), 'dd/MM/yyyy')}{' '}
                        {selectedFile?.created && <span className={styles.fileSidebarLabel}>{selectedFile?.created && formatDateDifferenc(selectedFile?.created)}</span>}
                    </p>
                </div>
                <div>
                    <label className={styles.fileSidebarLabel}>Posted by</label>
                    <p>{getUserDetail(selectedFile?.upload_by)}</p>
                </div>
                {selectedFile?.metadata_author && (
                    <div>
                        <label className={styles.fileSidebarLabel}>Author</label>
                        <p>{selectedFile?.metadata_author}</p>
                    </div>
                )}
                {selectedFile?.read_by?.length > 0 && (
                    <div>
                        <label className={styles.fileSidebarLabel}>Read by</label>
                        {selectedFile?.read_by?.map(item => (
                            <p className="m-0">{getUserNameByGroupMemberId(item)}</p>
                        ))}
                    </div>
                )}
                {hasHierarchicalKeywords(selectedFile?.keywords) && (
                    <div>
                        <label className={styles.fileSidebarLabel}>Keywords</label>
                        <p className="mb-0">{parseHierarchicalKeywordLabels(selectedFile.keywords).join(', ')}</p>
                    </div>
                )}

                {/* Lens AI Document Analysis Button */}
                <div className="mt-4">
                    <button
                        onClick={() => {
                            setShowLensAIAnalysis(true);
                        }}
                        type="button"
                        className={`btn btn-primary btn-sm w-100 d-flex align-items-center justify-content-center ${styles.smallButton}`}>
                        <Wand16Regular className="me-2" /> Lens Document Analysis
                    </button>
                </div>
            </div>

            {/* Lens AI Document Analysis Sidebar */}
            <OverlayDrawer
                style={{ minWidth: 872 }}
                type="overlay"
                position="end"
                open={showLensAIAnalysis}
                onOpenChange={(_, { open }) => {
                    setShowLensAIAnalysis(open);
                    if (!open) {
                        setSelectedAnalysisTab('executive-summary');
                    }
                }}>
                <DrawerBody className={`position-relative ${styles.drawerContent}`} style={{ padding: 0 }}>
                    <div className="d-flex align-items-center justify-content-between px-3 mt-3">
                        <div className="d-flex align-items-center">
                            <Wand20Regular color="var(--Brand-Secondary)" className="me-1" />
                            <span className={styles.aiTitle}>Lens AI Document Analysis</span>
                        </div>
                        <Dismiss20Regular onClick={() => setShowLensAIAnalysis(false)} role="button" color="var(--Neutral-Gray-6)" />
                    </div>

                    <div className={styles.analysisOptions}>
                        <div>
                            <span
                                onClick={() => setSelectedAnalysisTab('executive-summary')}
                                style={{
                                    color: selectedAnalysisTab === 'executive-summary' ? 'var(--Brand-Secondary)' : 'var(--Brand-Primary)',
                                    cursor: 'pointer',
                                }}
                                className={styles.analysisOption}>
                                Executive Summary
                            </span>
                            <span
                                onClick={() => setSelectedAnalysisTab('strengths')}
                                style={{
                                    color: selectedAnalysisTab === 'strengths' ? 'var(--Brand-Secondary)' : 'var(--Brand-Primary)',
                                    cursor: 'pointer',
                                }}
                                className={styles.analysisOption}>
                                Strengths
                            </span>
                            <span
                                onClick={() => setSelectedAnalysisTab('risks-gaps')}
                                style={{
                                    color: selectedAnalysisTab === 'risks-gaps' ? 'var(--Brand-Secondary)' : 'var(--Brand-Primary)',
                                    cursor: 'pointer',
                                }}
                                className={styles.analysisOption}>
                                Risks & Gaps
                            </span>
                            <span
                                onClick={() => setSelectedAnalysisTab('additional-insights')}
                                style={{
                                    color: selectedAnalysisTab === 'additional-insights' ? 'var(--Brand-Secondary)' : 'var(--Brand-Primary)',
                                    cursor: 'pointer',
                                }}
                                className={styles.analysisOption}>
                                Additional Insights
                            </span>
                            <span
                                onClick={() => setSelectedAnalysisTab('key-questions')}
                                style={{
                                    color: selectedAnalysisTab === 'key-questions' ? 'var(--Brand-Secondary)' : 'var(--Brand-Primary)',
                                    cursor: 'pointer',
                                }}
                                className={styles.analysisOption}>
                                Key Questions to Ask
                            </span>
                        </div>
                        <div
                            onClick={() => {
                                if (!aiAnalysisLoading) {
                                    generateNewAnalysis();
                                }
                            }}
                            style={{
                                color: aiAnalysisLoading ? 'var(--Neutral-Gray-6)' : 'var(--Brand-Secondary)',
                                cursor: aiAnalysisLoading ? 'not-allowed' : 'pointer',
                                opacity: aiAnalysisLoading ? 0.5 : 1,
                            }}
                            className="d-flex align-items-center ms-4"
                            role="button">
                            {aiAnalysisLoading ? (
                                <Wand20Regular
                                    className="me-1"
                                    style={{
                                        transition: 'color 0.3s ease',
                                        color: currentColor,
                                    }}
                                />
                            ) : (
                                <ArrowClockwise16Regular className="me-1" />
                            )}
                            <span className={styles?.regenerateText}>{aiAnalysisLoading ? 'Generating...' : 'Regenerate'}</span>
                        </div>
                    </div>

                    {/* Content area - AI Analysis Results */}
                    <div className="px-3 mt-4 position-relative" style={{ minHeight: '200px' }}>
                        {aiAnalysisLoading && (
                            <div className={styles.copilotLoader}>
                                <Wand20Regular
                                    style={{
                                        transition: 'color 0.3s ease', // Add transition for color change
                                        color: aiAnalysisLoading ? currentColor : 'inherit', // Change color based on loading state
                                    }}
                                />
                                <p>Loading results</p>
                            </div>
                        )}

                        {aiAnalysisError && (
                            <div className="alert alert-danger" role="alert">
                                <strong>Error:</strong> {aiAnalysisError}
                                <button type="button" className="btn btn-sm btn-outline-danger ms-2" onClick={callAIAnalysis}>
                                    Retry
                                </button>
                            </div>
                        )}

                        {!aiAnalysisLoading && !aiAnalysisError && aiAnalysisData && (
                            <div className={styles['analysis-content']}>
                                {selectedAnalysisTab === 'executive-summary' && (
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: getHtmlForTab(),
                                        }}
                                    />
                                )}

                                {selectedAnalysisTab === 'strengths' && (
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: getHtmlForTab(),
                                        }}
                                    />
                                )}

                                {selectedAnalysisTab === 'risks-gaps' && (
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: getHtmlForTab(),
                                        }}
                                    />
                                )}

                                {selectedAnalysisTab === 'additional-insights' && (
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: getHtmlForTab(),
                                        }}
                                    />
                                )}

                                {selectedAnalysisTab === 'key-questions' && (
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: getHtmlForTab(),
                                        }}
                                    />
                                )}
                            </div>
                        )}

                        {!aiAnalysisLoading && !aiAnalysisError && !aiAnalysisData && <div className={styles.noOptionSelected}>Click "Regenerate" to analyze this document.</div>}
                    </div>
                </DrawerBody>
            </OverlayDrawer>
        </div>
    );
};

export default FileInfoSidebar;
